/**
 * Отправка в NetBox через согласование: состояния запроса, права
 * участников, подписи. Сервер проверяет права сам — здесь только то,
 * что нужно интерфейсу, чтобы не показывать недоступные кнопки.
 *
 * Процесс:
 *   редактор заполняет данные и отмечает объект «Готово к отправке» →
 *   координатор создаёт запрос → контролёр данных одобряет (данные
 *   уходят в NetBox) или отклоняет с комментарием.
 */

/** Состояния запроса. tone — цвет плашки. */
export const REQUEST_STATUS = {
  pending: { label: 'На проверке', tone: 'info' },
  sending: { label: 'Отправляется', tone: 'info' },
  sent: { label: 'Отправлен', tone: 'ok' },
  partial: { label: 'Отправлен с ошибками', tone: 'warn' },
  rejected: { label: 'Отклонён', tone: 'err' },
  withdrawn: { label: 'Отозван', tone: 'muted' },
  stale: { label: 'Устарел', tone: 'muted' },
};

export const statusLabel = (status) => REQUEST_STATUS[status]?.label ?? status;
export const isOpen = (status) => status === 'pending' || status === 'sending';

export const ROLE_LABELS = {
  developer: 'Разработчик',
  coordinator: 'Координатор',
  controller: 'Контролёр данных',
  admin: 'Редактор',
  viewer: 'Просмотр',
};

/** Варианты роли в управлении пользователями — в порядке возрастания прав. */
export const ROLE_OPTIONS = [
  { value: 'viewer', label: 'Только просмотр' },
  { value: 'admin', label: 'Редактор — ведёт данные' },
  { value: 'coordinator', label: 'Координатор — создаёт запросы в NetBox' },
  { value: 'controller', label: 'Контролёр данных — одобряет запросы' },
  { value: 'developer', label: 'Полный доступ' },
];

export const EDIT_ROLES = new Set(['admin', 'coordinator', 'developer']);

export const canMarkReady = (user) => Boolean(user && EDIT_ROLES.has(user.role));

/** Раздел «Запросы в NetBox»: координатору, контролёру и разработчику.
 *  Редактору он не нужен — его шаг, «Готово к отправке», в окне объекта. */
export const REQUESTS_PAGE_ROLES = ['coordinator', 'controller', 'developer'];
export const canSeeRequests = (user) => Boolean(user && REQUESTS_PAGE_ROLES.includes(user.role));
export const canCreateRequest = (user) => user?.role === 'coordinator';

/** Принять решение: контролёр, по чужому запросу, пока он на проверке. */
export function canDecide(user, request) {
  return Boolean(user && request && user.role === 'controller'
    && request.status === 'pending' && request.createdBy !== user.username);
}

export function canRetry(user, request) {
  return Boolean(user && request && user.role === 'controller'
    && request.status === 'partial' && request.createdBy !== user.username);
}

export function canWithdraw(user, request) {
  return Boolean(user && request && user.role === 'coordinator'
    && request.status === 'pending' && request.createdBy === user.username);
}

/** Что из меню важно этой роли: контролёру — очередь, координатору — готовые объекты. */
export function badgeFor(user, counts) {
  if (!user || !counts) return 0;
  if (user.role === 'controller') return counts.pending || 0;
  if (user.role === 'coordinator') return counts.ready || 0;
  return 0;
}

const plural = (n, one, few, many) => {
  const m10 = n % 10, m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
};

/** Состав запроса одной строкой: «2 серийных номера, 1 стойка, …». */
export function describeCounts(c) {
  if (!c) return '';
  const parts = [];
  if (c.serials) parts.push(`${c.serials} ${plural(c.serials, 'серийный номер', 'серийных номера', 'серийных номеров')}`);
  if (c.racks) parts.push(`${c.racks} ${plural(c.racks, 'стойка', 'стойки', 'стоек')}`);
  if (c.placements) parts.push(`${c.placements} ${plural(c.placements, 'размещение', 'размещения', 'размещений')}`);
  if (c.moves) parts.push(`${c.moves} ${plural(c.moves, 'перенос', 'переноса', 'переносов')}`);
  if (c.devices) parts.push(`${c.devices} ${plural(c.devices, 'новое устройство', 'новых устройства', 'новых устройств')}`);
  return parts.join(', ') || 'нет изменений';
}

/** Блоки запроса из выбора в окне отправки и того, что есть в плане. */
export function blocksFromSelection(selection, plan) {
  if (!plan) return { serials: false, racks: false, moves: false, devices: false };
  const hasRacks = plan.racks.racks.some((r) => !r.existingId) || plan.racks.placements.length > 0
    || (selection.moves && plan.racks.moves.length > 0);
  return {
    serials: Boolean(selection.serials) && plan.serials.toSend.length > 0,
    racks: Boolean(selection.racks) && hasRacks && !plan.siteProblem,
    moves: Boolean(selection.racks && selection.moves) && plan.racks.moves.length > 0 && !plan.siteProblem,
    devices: Boolean(selection.devices) && plan.devices.toCreate.length > 0 && !plan.siteProblem,
  };
}

export const hasAnyBlock = (b) => Boolean(b.serials || b.racks || b.devices);

export function formatDateTime(ms) {
  if (!ms) return '—';
  const d = new Date(Number(ms));
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
